import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { apiService, APP_API_KEY } from '../services/api';

const API = process.env.NODE_ENV === 'production' ? '/api/inventory' : 'http://localhost:3001/api/inventory';

// Kleurlogica: rood = tekort, oranje = <2x, groen = 2x+
const getStockChipColor = (available, needed) => {
  if (available < needed) return { bg: '#d32f2f', text: '#fff' };
  if (available < needed * 2) return { bg: '#ed6c02', text: '#fff' };
  return { bg: '#2e7d32', text: '#fff' };
};

const PendingProducts = () => {
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [meta, setMeta] = useState({});
  const [stockMap, setStockMap] = useState({});
  
  // Refs for smooth scrolling
  const expandedProductRef = useRef(null);
  const selectedOrderRef = useRef(null);

  useEffect(() => {
    loadPendingProducts();
  }, []);

  const loadPendingProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const timestamp = Date.now();
      
      const authHeaders = { 'x-api-key': APP_API_KEY };
      const [response, stockRes, innostockRes] = await Promise.all([
        apiService.getPendingProducts({ _t: timestamp }),
        fetch(`${API}/stock`, { headers: authHeaders }).then(r => r.json()).catch(() => []),
        fetch(`${API}/innostock`, { headers: authHeaders }).then(r => r.json()).catch(() => []),
      ]);
      
      setPendingProducts(response.data || []);
      setMeta(response.meta || {});

      // Bouw stockMap: sku → { external, innostock }
      const map = {};
      (Array.isArray(stockRes) ? stockRes : []).forEach(s => {
        const sku = s.inventory_products?.sku;
        const wh = s.inventory_warehouses?.name;
        if (!sku) return;
        if (!map[sku]) map[sku] = { external: 0, innostock: 0 };
        if (wh !== 'InnoStock') map[sku].external += (s.quantity || 0);
      });
      (Array.isArray(innostockRes) ? innostockRes : []).forEach(p => {
        if (!p.sku) return;
        if (!map[p.sku]) map[p.sku] = { external: 0, innostock: 0 };
        map[p.sku].innostock = p.freestock || 0;
      });
      setStockMap(map);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'processing': 'info',
      'on-hold': 'error',
      'intern-magazijn': 'info',
      'logo-ok': 'success',
      'logo-print': 'warning',
      'logo-proofprint': 'warning',
      'logo-received': 'info',
      'design-request': 'warning',
      'logo-request': 'warning'
    };
    return colors[status] || 'default';
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      'pending': 'In afwachting',
      'processing': 'In behandeling',
      'on-hold': 'Wacht',
      'intern-magazijn': 'Magazijn HH',
      'logo-ok': 'Logo akkoord',
      'logo-print': 'Logo naar drukker',
      'logo-proofprint': 'Logo proefdruk',
      'logo-received': 'Logo ontvangen',
      'design-request': 'Design opvragen',
      'logo-request': 'Logo opvragen'
    };
    return labels[status] || status;
  };

  const handleAccordionChange = (productSku) => (event, isExpanded) => {
    setExpandedProduct(isExpanded ? productSku : false);
    // Close selected order when changing product
    if (!isExpanded) {
      setSelectedOrder(null);
    } else {
      // Scroll to expanded product section after state update
      setTimeout(() => {
        if (expandedProductRef.current) {
          expandedProductRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const handleOrderClick = async (order) => {
    // If same order is clicked, close it
    if (selectedOrder?.order_id === order.order_id) {
      setSelectedOrder(null);
      return;
    }

    try {
      // Fetch full order details from backend
      const orderDetailsResponse = await apiService.getOrderDetails(order.order_id);
      
      // Combine the order data with full order details
      const fullOrderData = {
        ...order,
        fullDetails: orderDetailsResponse.data
      };
      
      setSelectedOrder(fullOrderData);
      
      // Scroll to order details section after state update
      setTimeout(() => {
        if (selectedOrderRef.current) {
          selectedOrderRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 200); // Longer timeout to allow for loading
      
    } catch (error) {
      console.error('Error fetching order details:', error);
      // Fallback to basic order data
      setSelectedOrder(order);
      
      // Still scroll even with fallback data
      setTimeout(() => {
        if (selectedOrderRef.current) {
          selectedOrderRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Openstaande producten worden geladen...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={loadPendingProducts}>
            Opnieuw proberen
          </Button>
        }
      >
        Fout bij het laden van openstaande producten: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Openstaande Producten
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadPendingProducts}
          disabled={loading}
        >
          Vernieuwen
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InventoryIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Unieke Producten
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {pendingProducts.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ShoppingCartIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Totaal Aantal
                  </Typography>
                  <Typography variant="h4" color="warning">
                    {pendingProducts.reduce((sum, product) => sum + product.quantity_pending, 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InfoIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Openstaande Orders
                  </Typography>
                  <Typography variant="h4" color="info">
                    {meta.totalOrders || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {pendingProducts.length === 0 ? (
        <Box textAlign="center" py={8}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Geen openstaande producten gevonden
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Alle orders zijn afgerond of er zijn geen openstaande orders
          </Typography>
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {pendingProducts.length} unieke producten gevonden in {meta.totalOrders} openstaande orders
          </Alert>

          {/* Main Table */}
          <Paper sx={{ mb: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Omschrijving</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Aantal in Open Orders
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Extern
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      InnoStock
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Bekijk Bestellingen
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingProducts.map((product) => (
                    <TableRow 
                      key={product.sku || `product-${product.name}`} 
                      hover
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}
                    >
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {product.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={product.quantity_pending}
                          color="warning"
                          size="medium"
                          sx={{ fontWeight: 'bold', minWidth: '60px' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const stock = stockMap[product.sku] || { external: 0, innostock: 0 };
                          const needed = product.quantity_pending;
                          const colors = getStockChipColor(stock.external, needed);
                          return (
                            <Chip
                              label={stock.external}
                              size="medium"
                              sx={{ fontWeight: 'bold', minWidth: '60px', backgroundColor: colors.bg, color: colors.text }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const stock = stockMap[product.sku] || { external: 0, innostock: 0 };
                          const needed = product.quantity_pending;
                          const colors = getStockChipColor(stock.innostock, needed);
                          return (
                            <Chip
                              label={stock.innostock}
                              size="medium"
                              sx={{ fontWeight: 'bold', minWidth: '60px', backgroundColor: colors.bg, color: colors.text }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleAccordionChange(product.sku)(null, expandedProduct !== product.sku)}
                        >
                          {expandedProduct === product.sku ? 'Verberg' : 'Bekijk'} ({product.orders.length})
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Voetnoot kleurlegenda */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3, px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Voorraadkleur:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
              <Typography variant="caption" color="text.secondary">Ruim voldoende (≥ 2× benodigd)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#ed6c02' }} />
              <Typography variant="caption" color="text.secondary">Beperkte buffer (&lt; 2× benodigd)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
              <Typography variant="caption" color="text.secondary">Tekort (minder dan benodigd)</Typography>
            </Box>
          </Box>

          {/* Detailed Order Information */}
          {expandedProduct && (
            <Paper sx={{ mt: 2 }} ref={expandedProductRef}>
              {pendingProducts
                .filter(product => product.sku === expandedProduct)
                .map(product => (
                  <Box key={product.sku || `product-${product.name}`} p={3}>
                    <Typography variant="h6" gutterBottom>
                      Orders voor: {product.name} ({product.sku})
                    </Typography>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Order #</TableCell>
                            <TableCell>Klant</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Aantal</TableCell>
                            <TableCell>Datum</TableCell>
                            <TableCell align="center">Bekijk Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {product.orders.map((order) => {
                            // Helper function to get customer display name - ALWAYS use company name
                            const getCustomerDisplayName = (customerData) => {
                              // Always use the customer data from backend (which should be company name)
                              return customerData || 'Onbekend';
                            };
                            
                            const displayCustomerName = getCustomerDisplayName(order.customer);
                            
                            return (
                            <TableRow 
                              key={`${order.order_id}-${product.sku || product.name}`}
                              hover
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  #{order.order_number}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  ID: {order.order_id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {displayCustomerName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getOrderStatusLabel(order.status)}
                                  color={getOrderStatusColor(order.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight="bold">
                                  {order.quantity}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(order.date_created)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOrderClick(order)}
                                >
                                  {selectedOrder?.order_id === order.order_id ? 'Verberg' : 'Bekijk'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
            </Paper>
          )}

          {/* Selected Order Details */}
          {selectedOrder && (
            <Paper sx={{ mt: 2, p: 3 }} ref={selectedOrderRef}>
              <Typography variant="h6" gutterBottom>
                Order Details - #{selectedOrder.order_id}
              </Typography>
              
              {selectedOrder.fullDetails ? (
                <Grid container spacing={3}>
                  {/* Customer Details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Klantgegevens
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                      {/* Helper function to get customer display name - ALWAYS use company name */}
                      {(() => {
                        const getCustomerDisplayName = (billing) => {
                          return billing?.company || 'Onbekend';
                        };
                        
                        const customerName = getCustomerDisplayName(selectedOrder.fullDetails.billing);
                        return (
                          <>
                            <Typography variant="body2"><strong>Bedrijfsnaam:</strong> {customerName}</Typography>
                            <Typography variant="body2"><strong>Email:</strong> {selectedOrder.fullDetails.billing?.email || 'Niet opgegeven'}</Typography>
                            <Typography variant="body2"><strong>Telefoon:</strong> {selectedOrder.fullDetails.billing?.phone || 'Niet opgegeven'}</Typography>
                          </>
                        );
                      })()}
                      
                      <Box mt={1}>
                        <Typography variant="body2"><strong>Factuuradres:</strong></Typography>
                        <Typography variant="body2">
                          {selectedOrder.fullDetails.billing?.address_1} {selectedOrder.fullDetails.billing?.address_2}<br />
                          {selectedOrder.fullDetails.billing?.postcode} {selectedOrder.fullDetails.billing?.city}<br />
                          {selectedOrder.fullDetails.billing?.country}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  {/* Order Details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Order Informatie
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2"><strong>Order nummer:</strong> #{selectedOrder.fullDetails.number}</Typography>
                      <Typography variant="body2"><strong>Order datum:</strong> {formatDate(selectedOrder.fullDetails.date_created)}</Typography>
                      <Typography variant="body2"><strong>Status:</strong> {getOrderStatusLabel(selectedOrder.fullDetails.status)}</Typography>
                      <Typography variant="body2"><strong>Betaalmethode:</strong> {selectedOrder.fullDetails.payment_method_title || 'Niet opgegeven'}</Typography>
                      
                      <Box mt={1}>
                        <Typography variant="body2"><strong>Subtotaal:</strong> {formatCurrency(selectedOrder.fullDetails.total - selectedOrder.fullDetails.total_tax - selectedOrder.fullDetails.shipping_total)}</Typography>
                        <Typography variant="body2"><strong>Verzendkosten:</strong> {formatCurrency(selectedOrder.fullDetails.shipping_total)}</Typography>
                        <Typography variant="body2"><strong>BTW:</strong> {formatCurrency(selectedOrder.fullDetails.total_tax)}</Typography>
                        <Typography variant="body1"><strong>Totaal:</strong> {formatCurrency(selectedOrder.fullDetails.total)}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  {/* Order Items */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Bestelde Producten
                    </Typography>
                    <Paper>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell align="center">Aantal</TableCell>
                            <TableCell align="right">Prijs</TableCell>
                            <TableCell align="right">Totaal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.fullDetails.line_items?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {item.sku || 'Geen SKU'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {item.quantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatCurrency(item.price)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatCurrency(item.total)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                // Fallback for basic order data
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Klantgegevens
                    </Typography>
                    <Box>
                      <Typography variant="body2"><strong>Naam:</strong> {selectedOrder.customer}</Typography>
                      <Typography variant="body2"><strong>Order datum:</strong> {formatDate(selectedOrder.date_created)}</Typography>
                      <Typography variant="body2"><strong>Status:</strong> {getOrderStatusLabel(selectedOrder.status)}</Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Order Informatie
                    </Typography>
                    <Box>
                      <Typography variant="body2"><strong>Order ID:</strong> #{selectedOrder.order_id}</Typography>
                      <Typography variant="body2"><strong>Aantal:</strong> {selectedOrder.quantity}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
              
              <Box mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedOrder(null)}
                >
                  Sluiten
                </Button>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default PendingProducts;
